import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activitiesApi, membersApi, categoriesApi } from '../services/api';
import { ActivityRecord, ActivityInput, Member, ActivityCategoryVersion } from '../types/api';
import { Plus, Calendar, Award, User, Search, Filter, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity?: ActivityRecord;
  members: Member[];
  categories: ActivityCategoryVersion[];
}

const ActivityModal: React.FC<ActivityModalProps> = ({ 
  isOpen, 
  onClose, 
  activity, 
  members,
  categories 
}) => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<ActivityInput>({
    defaultValues: activity || {
      member_id: 0,
      category_version_id: 0,
      activity_date: new Date().toISOString().split('T')[0],
      tokens_awarded: undefined,
      notes: ''
    }
  });

  const selectedCategoryId = watch('category_version_id');
  const selectedCategory = categories.find(c => c.version_id === Number(selectedCategoryId));

  React.useEffect(() => {
    if (selectedCategory) {
      setValue('tokens_awarded', selectedCategory.token_value);
    }
  }, [selectedCategory, setValue]);

  const createMutation = useMutation({
    mutationFn: activitiesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['memberActivities'] });
      toast.success('Activity recorded successfully');
      onClose();
      reset();
    },
    onError: () => {
      toast.error('Failed to record activity');
    }
  });

  const onSubmit = (data: ActivityInput) => {
    createMutation.mutate({
      ...data,
      member_id: Number(data.member_id),
      category_version_id: Number(data.category_version_id)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {activity ? 'View Activity' : 'Record New Activity'}
        </h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-san-gray-700 mb-1">
              Member *
            </label>
            <select
              {...register('member_id', { required: 'Member is required' })}
              className="input-field"
              disabled={!!activity}
            >
              <option value="">Select a member</option>
              {members.map(member => (
                <option key={member.member_id} value={member.member_id}>
                  {member.name}
                </option>
              ))}
            </select>
            {errors.member_id && (
              <p className="text-red-500 text-sm mt-1">{errors.member_id.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-san-gray-700 mb-1">
              Activity Category *
            </label>
            <select
              {...register('category_version_id', { required: 'Category is required' })}
              className="input-field"
              disabled={!!activity}
            >
              <option value="">Select an activity</option>
              {categories.map(category => (
                <option key={category.version_id} value={category.version_id}>
                  {category.category_id}) {category.label}
                  {category.must_do && ' (Must-do)'}
                </option>
              ))}
            </select>
            {errors.category_version_id && (
              <p className="text-red-500 text-sm mt-1">{errors.category_version_id.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-san-gray-700 mb-1">
              Activity Date *
            </label>
            <input
              {...register('activity_date', { required: 'Date is required' })}
              type="date"
              className="input-field"
              disabled={!!activity}
            />
            {errors.activity_date && (
              <p className="text-red-500 text-sm mt-1">{errors.activity_date.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-san-gray-700 mb-1">
              Tokens Awarded
            </label>
            {selectedCategory && (
              <p className="text-sm text-san-gray-500 mt-1">
                Standard value: {selectedCategory.token_value} tokens
                {selectedCategory.max_tokens_per_cycle && (
                  ` (Max per cycle: ${selectedCategory.max_tokens_per_cycle})`
                )}
              </p>
            )}
            <input
              {...register('tokens_awarded', { 
                required: 'Tokens awarded is required',
                min: { value: 0, message: 'Tokens must be positive' }
              })}
              type="number"
              className="input-field"
              placeholder={selectedCategory ? `Default: ${selectedCategory.token_value}` : '0'}
              disabled={!!activity}
            />
            {errors.tokens_awarded && (
              <p className="text-red-500 text-sm mt-1">{errors.tokens_awarded.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-san-gray-700 mb-1">
              Notes
            </label>
            <textarea
              {...register('notes')}
              className="input-field"
              rows={3}
              placeholder="Additional details about this activity"
              disabled={!!activity}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              {activity ? 'Close' : 'Cancel'}
            </button>
            {!activity && (
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="btn-primary"
              >
                {createMutation.isPending ? 'Recording...' : 'Record Activity'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export const Activities: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingActivity, setViewingActivity] = useState<ActivityRecord | undefined>();
  const [selectedMember, setSelectedMember] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const { data: members } = useQuery({ queryKey: ['members'], queryFn: membersApi.getAll });
  const { data: categories } = useQuery({ queryKey: ['categories-2025'], queryFn: () => categoriesApi.getVersionsByYear(2025) });
  
  const { data: memberActivities, isLoading } = useQuery({
    queryKey: ['memberActivities', selectedMember],
    queryFn: () => selectedMember ? activitiesApi.getMemberActivities(selectedMember) : Promise.resolve([]),
    enabled: !!selectedMember
  });

  // Get all activities when "All Members" is selected
  const { data: allActivities, isLoading: allActivitiesLoading } = useQuery({
    queryKey: ['allActivities'],
    queryFn: activitiesApi.getAll,
    enabled: selectedMember === 0
  });

  // Use appropriate data source based on selection
  const activitiesData = selectedMember === 0 ? allActivities : memberActivities;
  const isLoadingActivities = selectedMember === 0 ? allActivitiesLoading : isLoading;

  const handleRecordActivity = () => {
    setViewingActivity(undefined);
    setIsModalOpen(true);
  };

  const handleViewActivity = (activity: ActivityRecord) => {
    setViewingActivity(activity);
    setIsModalOpen(true);
  };

  const filteredActivities = activitiesData?.filter(activity => {
    const member = members?.find(m => m.member_id === activity.member_id);
    const matchesSearch = !searchTerm || 
      member?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDate = !dateFilter || 
      activity.activity_date.startsWith(dateFilter);

    return matchesSearch && matchesDate;
  }) || [];

  // Calculate statistics
  const totalTokens = filteredActivities.reduce((sum, activity) => sum + activity.tokens_awarded, 0);
  const totalActivities = filteredActivities.length;
  const uniqueMembers = new Set(filteredActivities.map(a => a.member_id)).size;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-san-gray-900 sm:text-3xl sm:truncate">
            Activities
          </h1>
          <p className="mt-1 text-sm text-san-gray-500">
            Track and manage member activities and token awards
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button onClick={handleRecordActivity} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Record Activity
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="stat-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-san-blue" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="stat-label">Total Activities</dt>
                <dd className="stat-value">{totalActivities}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Award className="h-8 w-8 text-san-green" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="stat-label">Total Tokens</dt>
                <dd className="stat-value">{totalTokens.toLocaleString()}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <User className="h-8 w-8 text-san-blue" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="stat-label">Active Members</dt>
                <dd className="stat-value">{uniqueMembers}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Award className="h-8 w-8 text-san-green" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="stat-label">Avg Tokens</dt>
                <dd className="stat-value">
                  {totalActivities > 0 ? Math.round(totalTokens / totalActivities) : 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-san-gray-700 mb-1">
              Select Member
            </label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(Number(e.target.value))}
              className="input-field"
            >
              <option value={0}>All Members</option>
              {members?.map(member => (
                <option key={member.member_id} value={member.member_id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-san-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-san-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-san-gray-700 mb-1">
              Date Filter
            </label>
            <input
              type="month"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="input-field"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setDateFilter('');
                setSelectedMember(0);
              }}
              className="btn-secondary w-full"
            >
              <Filter className="w-4 h-4 mr-2" />
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Activities Table */}
      <div className="card">
        {isLoadingActivities ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-san-green"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-san-gray-900">
                Activity Records
              </h3>
              <span className="text-sm text-san-gray-500">
                {filteredActivities.length} activities found
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-san-gray-200">
                <thead className="bg-san-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                      Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-san-gray-200">
                  {filteredActivities.map((activity) => {
                    const member = members?.find(m => m.member_id === activity.member_id);
                    const category = categories?.find(c => c.version_id === activity.category_version_id);
                    
                    return (
                      <tr key={activity.record_id} className="hover:bg-san-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-san-gray-900">
                            {member?.name || 'Unknown Member'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-san-gray-900">
                            Category {category?.category_id || activity.category_version_id}
                          </div>
                          {category?.must_do && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Must-do
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-san-gray-900">
                            {format(new Date(activity.activity_date), 'MMM d, yyyy')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Award className="w-4 h-4 text-san-green mr-1" />
                            <span className="text-sm font-medium text-san-gray-900">
                              {activity.tokens_awarded.toLocaleString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-san-gray-500 max-w-xs truncate">
                            {activity.notes || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewActivity(activity)}
                            className="text-san-blue hover:text-san-blue-dark mr-3"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredActivities.length === 0 && (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-san-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-san-gray-900">No activities found</h3>
                <p className="mt-1 text-sm text-san-gray-500">
                  {selectedMember 
                    ? 'No activities recorded for the selected member.'
                    : 'Get started by recording your first activity.'
                  }
                </p>
                <div className="mt-6">
                  <button onClick={handleRecordActivity} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Record Activity
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ActivityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        activity={viewingActivity}
        members={members || []}
        categories={categories || []}
      />
    </div>
  );
};